import { InfoIcon } from "@langfuse/icons";
import { api } from "@/src/utils/api";
import { usdFormatter } from "@/src/utils/numbers";
import { Skeleton } from "@/src/components/ui/skeleton";
import { CustomTooltip } from "@/src/components/design-system/CustomTooltip/CustomTooltip";

type EstimatedCostRowProps = {
  projectId: string;
  evaluators: Array<{ id: string; name: string }>;
  observationCount: number;
};

function formatCostEstimate(cost: number): string {
  if (cost > 0 && cost < 0.005) return "< $0.01";
  return `~${usdFormatter(cost, 2, 2)}`;
}

export function EstimatedCostRow(props: EstimatedCostRowProps) {
  const { projectId, evaluators, observationCount } = props;

  const evaluatorIds = evaluators.map((e) => e.id);

  const avgCostQuery = api.evals.avgCostByEvaluatorIds.useQuery(
    { projectId, evaluatorIds },
    { enabled: evaluators.length > 0 },
  );

  if (avgCostQuery.isLoading) {
    return (
      <div className="flex gap-2">
        <span className="text-muted-foreground shrink-0">
          Est. LLM API Key Cost:
        </span>
        <Skeleton className="h-4 w-16" />
      </div>
    );
  }

  const data = avgCostQuery.data;
  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="flex gap-2">
        <span className="text-muted-foreground shrink-0">
          Est. LLM API Key Cost:
        </span>
        <span className="text-muted-foreground">No data</span>
      </div>
    );
  }

  const evaluatorsWithData = evaluatorIds.filter((id) => id in data);
  const evaluatorsWithoutData = evaluatorIds.filter((id) => !(id in data));
  const isPartial = evaluatorsWithoutData.length > 0;

  const totalEstimate = evaluatorsWithData.reduce(
    (sum, id) => sum + data[id].avgCost * observationCount,
    0,
  );

  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground shrink-0">
        Est. LLM API Key Cost:
      </span>
      <span className="flex items-center gap-1 font-bold">
        {formatCostEstimate(totalEstimate)}
        {isPartial ? "*" : ""}
        <CustomTooltip
          content={
            <div className="space-y-2">
              <p className="text-xs">
                Expected cost on your linked API key (not Langfuse). Estimated
                from average evaluator execution cost over the last 7 days.
              </p>
              <div className="space-y-1">
                {evaluators.map(({ id, name }) => {
                  const entry = data[id];
                  return (
                    <div
                      key={id}
                      className="flex justify-between gap-4 text-xs"
                    >
                      <span className="truncate" title={name}>
                        {name}
                      </span>
                      <span className="shrink-0 tabular-nums">
                        {entry
                          ? formatCostEstimate(entry.avgCost * observationCount)
                          : "No data"}
                      </span>
                    </div>
                  );
                })}
              </div>
              {isPartial ? (
                <p className="text-muted-foreground text-xs">
                  *Partial estimate. Some evaluators have no execution history.
                </p>
              ) : null}
            </div>
          }
        >
          {({ getTriggerProps }) => (
            <InfoIcon {...getTriggerProps()} size="sm" color="muted" />
          )}
        </CustomTooltip>
      </span>
    </div>
  );
}
